<template>
  <div class="session-times">
    <v-container v-if='!invalidSession'>
      <v-layout justify-start style='color: black; background-color: white; padding: 1vh 0 0 1vh;'>
        <h2>{{ sessionName }}</h2>
      </v-layout>
      <v-row>
        <v-col cols='8' class='flex-grow-0 flex-shrink-1' style='min-width: 40%'>
          <v-data-table
            class='data-table'
            :items-per-page="isStreamerMode ? streamerSettings.itemsPerPage + statsToCompute.length : Infinity"
            :headers='solverTimesHeader'
            :items='solverTimesData'
            hide-default-footer
            hide-default-header>
            <template v-slot:header='{props}'>
              <tr>
                <th v-for='header in props.headers' :key='header.id' style='text-align:center' :class='header.class' @click='addNewOwner(header.id, header.text)'>
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:item='{item, headers}'>
              <tr :class="[item.isRoundBarrier ? 'round-barrier' : '']">
                <td @click='deleteScramble(item.pos)'> {{ item.num }} </td>
                <td v-for='(header, index) in headers.slice(1)' :key='index'>
                  <!-- use the index to find the solverId which is used for updating solve metadata -->
                  <session-single-time v-if='item.pos >= 0 && item.times[header.id]'
                    :class="[isStreamerMode ? (winners[item.pos] === '' ? 'draw' : (winners[item.pos] === header.id ? 'winner' : 'loser')) : '']"
                    :time='item.times[header.id]'
                    :solverId='header.id'
                    :scrambleId='solves[item.pos]._id'
                    :scramble='solves[item.pos].scramble'
                    @open-dialog="$emit('open-dialog')" @close-dialog="$emit('close-dialog')" />
                  <!-- set up the class as item.num so special formatting can be used for streak/score/etc. -->
                  <session-single-time v-else-if='item.times[header.id]'
                    :class="['stat-item', item.num]"
                    :time='item.times[header.id]'
                    :average='item.num.toString()' />
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
        <v-col cols='4' class='flex-grow-1 flex-shrink-0' style='min-width: 30%; max-width: 60%'>
          <v-data-table
            class='stats-table'
            :items-per-page='Infinity'
            :header-props="{'sort-icon': 'fas fa-caret-down'}"
            :headers="[{
                text: 'Solvers',
                align: 'left',
                sortable: false,
                value: 'solver'
              }].concat(statsToCompute.map((v, i) => ({
                text: v,
                align: 'center',
                sortable: !isStreamerMode,
                value: i,
              })))"
            :items="solvers.map((solver) => ({
              solver: solver.solver,
              times: computedStats.map(v => (v.times[solver._id] && v.times[solver._id].time) || 0),
              style: statsToCompute,
              best: computedStats.map(v => (v.times[solver._id] && v.times[solver._id].best) || 0),
            }))"
            :custom-sort='(items, [i], [isDesc]) => {
              if (i !== null) {
                items.sort((a, b) => {
                  if (!a.times[i]) return 1
                  if (!b.times[i]) return -1
                  if (!isDesc) {
                    return a.times[i] - b.times[i]
                  } else {
                    return b.times[i] - a.times[i]
                  }
                })
              }
              return items
            }'
            hide-default-footer>
            <template v-slot:item='{item}'>
              <tr>
                <td>{{item.solver}}</td>
                <session-single-stat v-for='(time, index) in item.times' :key='index' :time='time' :best='item.best[index]' :styling='item.style[index]' />
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <v-data-table
          v-if='monkeyLeagueSeason'
          class='mlrounds-table'
          :headers='mlroundsHeaders'
          :items='Object.values(monkeyLeagueRounds)'
          disable-sort
          hide-default-footer
        >
          <template v-slot:item='{item}'>
            <tr>
              <td style='text-align: left'>{{item.name}}</td>
              <td v-for='(score, index) in item.scores.slice(Math.max(0, item.scores.length - (isStreamerMode ? streamerSettings.roundsCount : Infinity)))' :key='index' :class='{winner: score.winner, loser: score.loser, overtime: score.overtime}'>{{score.score}}</td>
            </tr>
          </template>
        </v-data-table>
      </v-row>
    </v-container>
    <div v-if='invalidSession'> Invalid Session! Either you're not properly logged in or we couldn't find a session with this link... </div>
  </div>
</template>

<script>
import buffer from '@/modules/buffer'
import cloneDeep from 'lodash/cloneDeep'

import SessionsService from '@/services/SessionsService'
import ScramblesService from '@/services/ScramblesService'

import SessionSingleTime from '@/components/SessionSingleTime'
import SessionSingleStat from '@/components/SessionSingleStat'
export default {
  name: 'session-times',
  data () {
    return {
      solvers: [],
      solves: [],
      sessionName: '',
      owners: [],
      invalidSession: false,

      // allow new data to overwrite the existing one (should be false when timer is running)
      // store the synced data in a queue until stop-timer is emitted by timer-base
      syncData: true,
      newDataQueue: [],

      // session times settings
      statsToCompute: [],

      // optimizations for recomputing stats
      computedStats: [],
    }
  },
  methods: {
    async initSessionTimes () {
      var response = await SessionsService.fetchTimes(this.$store.state.sessionId, {
        solverId: this.$store.state.solverId,
        authToken: this.$store.state.authToken,
        globalAuthToken: this.$store.state.globalAuthToken
      })
      var session = response.data.session
      if (session === undefined || session === null) {
        // either the sessionId is invalid or it is not present in the db
        this.invalidSession = true
      } else {
        this.invalidSession = false
        this.solvers = session.solvers
        this.solves = session.solves.map((v) => ({
          ...v,
          times: v.times.reduce((acc, time, i) => ({...acc, [session.solvers[i]._id]: time}), {})
        }))
        this.owners = session.owners
        this.sessionName = session.sessionName

        this.$store.commit('setSessionType', session.sessionType)
        if (this.solves.length === 0) {
          this.$store.commit('setScramble', 'No scrambles found yet. Press next to create one')
        } else {
          this.$store.commit('setScramble', this.solves[this.solves.length - 1].scramble)
          this.$store.commit('setScrambleId', this.solves[this.solves.length - 1]._id)
        }
        this.computedStats = new Array(this.statsToCompute.length).fill().map((_, i) => this.solvers.reduce((acc, v) => {acc.times = {...acc.times, [v._id]: {time: 0}}; return acc}, {num: this.statsToCompute[i], pos: i - this.statsToCompute.length, times: {}}))
        for (let i in this.solvers) {
          this.updateAveragesStats(this.solvers[i]._id)
        }
        this.updateWinnerStats()
      }
    },
    getSolveTime(solveTime) {
      if (solveTime === undefined || (solveTime.time === 0 && !solveTime.DNF)) return NaN
      else if (solveTime.DNF) return Infinity
      else if (solveTime.plusTwo) return (solveTime.time + 2)
      else return solveTime.time
    },
    async addNewOwner (newOwnerId, newOwnerName) {
      let res = confirm('You are about to make ' + newOwnerName + ' an owner for this session. They will be able to generate new scrambles but cannot invite new users, change the password, or add new owners. You will still be an owner. THIS CANNOT BE UNDONE. Press OK to confirm')
      if (res === true) {
        let response = await SessionsService.addOwner(this.$store.state.sessionId, {
          solverId: this.$store.state.solverId,
          authToken: this.$store.state.authToken,
          globalAuthToken: this.$store.state.globalAuthToken,
          newOwnerId: newOwnerId,
          newOwnerName: newOwnerName
        })
        if (response.data.success) {
          this.owners.push(response.data.newOwner)
          alert('Success!')
        } else {
          alert('Error: ' + response.data.error)
        }
      }
    },
    async deleteScramble (scramblePos) {
      if (scramblePos < 0) return

      const scrambleId = this.solves[scramblePos]._id
      let res = confirm('You are about to delete a scramble for all users. THIS CANNOT BE UNDONE. Press OK to confirm')
      if (res === true) {
        let response = await ScramblesService.deleteScramble(this.$store.state.sessionId, {
          solverId: this.$store.state.solverId,
          authToken: this.$store.state.authToken,
          globalAuthToken: this.$store.state.globalAuthToken,
          scrambleId: scrambleId,
        })
        if (response.data.success) {
          alert('Success!')
        } else {
          alert('Error: ' + response.data.error)
        }
      }
    },
    addTimeToSession (params) {
      if (!this.syncData) {
        this.newDataQueue.push({
          type: 'time',
          solverId: params.solverId,
          scrambleId: params.scrambleId,
          time: params.time
        })
        return
      }

      let scramblePos = -1
      for (let i = this.solves.length - 1; i >= 0; i--) {
        if (this.solves[i]._id === params.scrambleId) {
          scramblePos = i
          this.solves[i].times[params.solverId] = params.time // set the correct time
          this.$set(this.solves, i, this.solves[i]) // using $set instead of $forceUpdate to allow computed properties to refresh
          break
        }
      }
      this.updateAveragesStats(params.solverId, scramblePos)
      this.updateWinnerStats()
      // NOTE: if no solve was updated, there is an inconsistent scramble, ie, client didn't receive a scramble that exists

      // check if current scramble is the latest one (so that a bad actor can't just repeat old scrambles to force owner to generate new scrambles),
      // solverID is index 0 (ie, owner) && check if all solvers have finished => generate new scramble
      if (params.scrambleId === this.solves[this.solves.length - 1]._id 
        && this.currentSolverPosition === 0) {
        // in Monkey League, we pause generating scrambles at round barriers
        if (this.monkeyLeagueSeason) {
          const roundEnds = this.monkeyLeagueSeason && !this.isStreamerMode && Object.keys(this.monkeyLeagueRounds)[0] && this.monkeyLeagueRounds[Object.keys(this.monkeyLeagueRounds)[0]].scores.map(v => v.roundEnd)
          if (roundEnds.length > 0 && roundEnds[roundEnds.length - 1] === this.solves.length) return
        }

        let i = this.solves.length - 1
        for (let j in this.solves[i].times) {
          if (!this.solves[i].times[j] || !this.solves[i].times[j].time && !this.solves[i].times[j].DNF) {
            return
          }
        }
        // if we haven't returned yet then it means all times have been submitted for latest scramble
        ScramblesService.generateScramble(this.$store.state.sessionId, {
          solverId: this.$store.state.solverId,
          authToken: this.$store.state.authToken,
          globalAuthToken: this.$store.state.globalAuthToken
        }).then()
      }
    },
    addSolverToSession (solver) {
      if (!this.syncData) {
        this.newDataQueue.push({
          type: 'solver',
          solver: solver.solver,
          solverId: solver.solverId
        })
        return
      }

      for (let i = 0; i < this.solvers.length; i++) {
        if (this.solvers[i]._id === solver.solverId) {
          return
        }
      }

      this.statsToCompute.forEach((_, i) => this.computedStats[i].times[solver.solverId] = {time: 0})

      this.solvers.push({
        _id: solver.solverId,
        solver: solver.solver
      })
    },
    addScrambleToSession (scramble) {
      if (!this.syncData) {
        this.newDataQueue.push({
          type: 'scramble',
          scramble: scramble.scramble,
          scrambleId: scramble.scrambleId
        })
        return
      }

      this.$store.commit('setScramble', scramble.scramble)
      this.$store.commit('setScrambleId', scramble.scrambleId)
      this.$store.commit('setTime', 0)
      this.$store.commit('setComment', '')

      for (let i = 0; i < this.solves.length; i++) {
        if (this.solves[i]._id === scramble.scrambleId) {
          return
        }
      }
      this.solves.push({
        _id: scramble.scrambleId,
        scramble: scramble.scramble,
        times: this.solvers.reduce((acc, v) => ({...acc, [v._id]: { time: 0, plusTwo: false, DNF: false, comment: '' }}), {}),
      })
      this.updateWinnerStats()
    },
    deleteScrambleFromSession (scramble) {
      if (!this.syncData) {
        this.newDataQueue.push({
          type: 'delete',
          scrambleId: scramble.scrambleId
        })
        return
      }
      console.log(scramble)

      this.solves = this.solves.filter((v) => (v._id !== scramble.scrambleId))
      
      this.redoLatestSolve()
      this.updateWinnerStats()
    },
    overwriteData () {
      // TODO: fix this to remove the timeout
      // setTimeout to give UI some time to catch up before doing work
      setTimeout(() => {
        this.syncData = true
        while (this.newDataQueue.length !== 0) {
          let data = this.newDataQueue.shift()
          if (data.type === 'time') {
            this.addTimeToSession(data)
          } else if (data.type === 'solver') {
            this.addSolverToSession(data)
          } else if (data.type === 'scramble') {
            this.addScrambleToSession(data)
          } else if (data.type === 'delete') {
            this.deleteScrambleFromSession(data)
          }
        }
      }, 350)
    },
    redoLatestSolve () {
      const latestSolve = this.solves[this.solves.length - 1]
      const latestTime = latestSolve && latestSolve.times[this.$store.state.solverId] || {time: 0, comment: ''}
      this.$store.commit('setScrambleId', latestSolve._id)
      this.$store.commit('setScramble', latestSolve.scramble)
      this.$store.commit('setTime', latestTime.time)
      this.$store.commit('setComment', latestTime.comment)
    },
    updateAveragesStats (solverId, scramblePos) {
      if (scramblePos === -1) return
      const updateAllTimes = scramblePos === undefined
      this.statsToCompute.forEach((avg, i) => {
        let matches = avg.match(/(A|M)o(\d*)/i)
        if (avg === 'time') matches = ['time', 'M', '1']
        else if (avg === 'median') matches = ['median', 'ME', Infinity]
        else if (avg === 'streak') matches = ['streak', 'ST', '1']
        else if (avg === 'score') matches = ['score', 'SC', '1']
        else if (avg === 'mlscore') matches = ['mlscore', 'LSC', '1']
        else if (avg === 'mlstat') matches = ['mlstat', 'LST', '1']
        else if (avg === 'mlcurr') matches = ['mlcurr', 'LSR', '1']

        if (!matches || matches.length !== 3) {
          this.computedStats[i].times[solverId] = {time: 0, best: 0}
          return
        }
        // TODO: remove these multiple or statements from here. can we just use the !matches to set a temp value for computedStats?
        if (avg === 'streak' || avg === 'score' || avg === 'mlscore' || avg === 'mlstat' || avg === 'mlcurr') {
          return
        }

        let currentStat = this.currentAvg(solverId, scramblePos, updateAllTimes, matches[1], Math.floor(parseFloat(matches[2])))
        if (!this.computedStats[i].times[solverId].best) this.computedStats[i].times[solverId].best = Infinity
        this.computedStats[i].times[solverId] = {
          time: currentStat.impactsCurr && !isNaN(currentStat.time) ? currentStat.time : this.computedStats[i].times[solverId].time,
          best: Math.min(this.computedStats[i].times[solverId].best, currentStat.best)
        }
      })
    },
    currentAvg (solverId, scramblePos, updateAllTimes, statType, N) {
      let start = updateAllTimes ? 0 : Math.max(0, scramblePos - N + 1)
      let end = updateAllTimes ? this.solves.length - 1 : Math.min(this.solves.length - 1, scramblePos + N - 1)
      
      let time = NaN
      let best =  Infinity
      let lastNTimes = buffer.createRingBuffer((N === Infinity) ? this.solves.length : N)

      // set the correct start position (go until we see at least N solves before)
      numCounted = 0
      while(numCounted < N && start != 0) {
        const currTime = this.solves[start].times[solverId]
        if (currTime === undefined) numCounted++
        else if (currTime.time === 0 && !currTime.DNF) numCounted++
        start--
      }
      // set the correct end position (go until we see at least N solves after and continue till end so we can set correct value for impactsCurr)
      let numCounted = 0
      while(numCounted <= N && end != this.solves.length - 1) {
        const currTime = this.solves[end].times[solverId]
        if (currTime === undefined) numCounted++
        else if (currTime.time === 0 && !currTime.DNF) numCounted++
        end++
      }

      for (let i = start; i <= end; i++) {
        const currTime = this.getSolveTime(this.solves[i].times[solverId])
        if (isNaN(currTime)) continue
        else lastNTimes.push(currTime)

        let stat = NaN
        if (lastNTimes.all.length === N) {
          let timesInAvg = lastNTimes.all.slice()
          let numRemoved = 0 // for Mean by default don't remove any solves
          let sum = 0
          if (statType === 'A') {
            // for Average sort the solves and remove top/bottom solves
            timesInAvg.sort((a, b) => a - b)
            // remove the lowest and highest 5% of solves (rounded up)
            numRemoved = Math.ceil(0.05 * N)
          }
          for (let j = numRemoved; j < N - numRemoved; j++) {
            sum += (timesInAvg[j]) * 100
          }
          stat = (sum / (N - 2 * numRemoved)) / 100
        }
        if (!isNaN(stat)) {
          time = stat
          best = Math.min(best, stat)
        }
      }
      if (N === Infinity && statType === 'ME') {
        let timesInAvg = lastNTimes.all.slice()
        timesInAvg.sort((a, b) => a - b)
        const timesInMedian = timesInAvg.slice(Math.floor((timesInAvg.length-1)/2), Math.ceil((timesInAvg.length-1)/2) + 1)
        time = best = timesInMedian.reduce((acc, v) => (acc + v), 0) / timesInMedian.length
      }
      return {time, best, impactsCurr: (end === this.solves.length - 1)}
    },
    updateWinnerStats () {
      this.statsToCompute.forEach((avg, i) => {
        if (!['streak', 'score', 'mlscore', 'mlstat', 'mlcurr'].includes(avg)) {
          return
        }
        this.solvers.forEach((solver) => {
          let solverId = solver._id
          let currentTime = 0
          if (avg === 'streak') currentTime = this.currentStreak[solverId]
          else if (avg === 'score') currentTime = this.currentScore[solverId]
          else if (avg === 'mlscore') currentTime = this.monkeyLeagueScore[solverId]
          else if (avg === 'mlstat') {
            // NOTE: mlstat is special and stores the actual time Object instead of just a single value
            this.computedStats[i].times[solverId] = this.monkeyLeagueStat[solverId]
            return
          }
          else if (avg === 'mlcurr') {
            const scores = this.monkeyLeagueRounds[solverId].scores
            this.computedStats[i].times[solverId] = {
              time: scores.length ? scores[scores.length - 1].score : 0,
            }
            return
          }
          if (!this.computedStats[i].times[solverId].best) this.computedStats[i].times[solverId].best = 0
          this.computedStats[i].times[solverId] = {
            time: currentTime,
            best: Math.max(this.computedStats[i].times[solverId].best, currentTime)
          }
        })
      })
    },
  },
  computed: {
    // data for table header
    solverTimesHeader () {
      let solverPosition = this.currentSolverPosition
      if (!this.solvers || this.solvers.length === 0) return []
      if (solverPosition === -1) {
        solverPosition = 0 // for spectator mode, use the default order instead of setting "current solver" in first column
      }

      let names = []
      names.push({
        text: '#',
        id: 'num',
        align: 'center',
        sortable: false
      })
      for (let i = 0; i < this.solvers.length; i++) {
        names.push({
          text: this.solvers[i].solver + (this.owners.some((a) => { return this.solvers[i]._id === a.ownerId }) ? '♛' : ''),
          id: this.solvers[i]._id,
          align: 'center',
          sortable: false,
          class: this.solvers[i]._id === this.$store.state.solverId ? 'bold-solver' : ''
        })
      }
      return [names[0]].concat([names[solverPosition + 1]]).concat(names.slice(1, solverPosition + 1)).concat(names.slice(solverPosition + 2))
    },
    // data for table data
    solverTimesData () {
      if (!this.solvers || this.solvers.length === 0) return []

      let data = cloneDeep(this.computedStats)

      // For normal viewers, reverse the list to display solves in reverse chronological order with latest solve at the top
      let i = this.solves.length - 1
      let end = 0
      // For streamers, set the list in ascending order instead so latest time appears at bottom
      if (this.isStreamerMode) {
        i = this.solves.length - this.streamerSettings.itemsPerPage
        end = this.solves.length - 1
        if (this.monkeyLeagueSeason) {
          i = (Math.ceil(this.solves.length / this.streamerSettings.itemsPerPage) - 1) * this.streamerSettings.itemsPerPage
          end = i + this.streamerSettings.itemsPerPage - 1
        }
      }
      const roundEnds = this.monkeyLeagueSeason && !this.isStreamerMode && Object.keys(this.monkeyLeagueRounds)[0] && this.monkeyLeagueRounds[Object.keys(this.monkeyLeagueRounds)[0]].scores.map(v => v.roundEnd)
      while ((!this.isStreamerMode && i >= end) || (this.isStreamerMode && i <= end)) {
        data.push({
          num: i + 1,
          times: (this.solves[i] && this.solves[i].times) || {},
          pos: i,
          isRoundBarrier: roundEnds && roundEnds.includes(i),
        })
        if (!this.isStreamerMode) {
          i--
        } else {
          i++
        }
      }
      return data
    },
    winners () {
      // winners (list of winner solverIds). used for streak and time calculation
      if (!this.solves) return []
      let winners = []
      for (let i = this.solves.length - 1; i >= 0; i--) {
        let minTime = Infinity, minIndex = ''
        let numCompleted = 0
        Object.entries(this.solves[i].times).forEach(([id, solveTime]) => {
          let currTime = this.getSolveTime(solveTime)

          if (!isNaN(currTime)) numCompleted++

          if (currTime < minTime) {
            minIndex = id
            minTime = currTime
          } else if (currTime === minTime && minTime !== Infinity) {
            // In case of tie, no one wins
            minIndex = ''
            minTime = 0
          }
        })
        winners[i] = (i !== this.solves.length - 1 || numCompleted === Object.keys(this.solves[i].times).length) ? minIndex : ''
      }
      return winners
    },
    currentStreak () {
      // TODO: support getting best historical streak (currently the best streak is forgotten after page refresh)
      const winners = this.winners.filter(pos => pos !== '')
      let streak = this.solvers ? this.solvers.reduce((acc, v) => ({...acc, [v._id]: 0}), {}) : {}
      let currWinner = ''
      if (winners.length > 0) {
        currWinner = winners[winners.length - 1]
        streak[currWinner]++
      }
      for (let i = winners.length - 2; i >= 0; i--) {
        if (winners[i] !== currWinner) break
        streak[currWinner]++
      }

      return streak
    },
    currentScore () {
      return this.winners.reduce((acc, solverId) => {
        if (acc[solverId] === undefined) return acc
        acc[solverId]++
        return acc
      }, this.solvers ? this.solvers.reduce((acc, v) => ({...acc, [v._id]: 0}), {}) : {})
    },

    // computed render properties
    isStreamerMode () {
      return this.$route.params.viewerType === 'stream'
    },
    streamerSettings () {
      if (!this.isStreamerMode) return undefined
      return {
        itemsPerPage: parseInt(this.$route.query.rows) || Infinity,
        roundsCount: parseInt(this.$route.query.rounds) || Infinity,
      }
    },
    currentSolverPosition () {
      let pos = -1
      for (let i = 0; i < this.solvers.length; i++) {
        if (this.solvers[i]._id === this.$store.state.solverId) {
          pos = i
          break
        }
      }
      return pos
    },

    // Monkey League properties
    monkeyLeagueSeason () {
      return parseInt(this.$route.query.mlseason) || false
    },
    mlWinScore () {
      return this.$route.query.winscore || 25
    },
    mlRoundWinScore () {
      return this.$route.query.roundwinscore || 3
    },
    mlroundsHeaders () {
      let roundNums = Object.values(this.monkeyLeagueRounds)[0] ? Object.values(this.monkeyLeagueRounds)[0].scores.map((_,i) => ({text: `${i + 1}`, id: `${i}`})) : []
      return [{text: 'name', id: 'name'}].concat(roundNums.slice(Math.max(0, roundNums.length - (this.isStreamerMode ? this.streamerSettings.roundsCount : Infinity))))
    },
    monkeyLeagueScore () {
      return Object.entries(this.monkeyLeagueRounds).reduce((acc, [id, v]) => {
        acc[id] = v.total
        return acc
      }, {})
    },
    monkeyLeagueStat () {
      let mlStat = this.solvers.reduce((acc, v) => ({...acc, [v._id]: {time: 0, type: ''}}), {})
      if (this.solves.length === 0) return mlStat

      // current solveNumInRound is based on the previous solve that was completed + 1 if all the solvers have finished the current solve
      const solveNumInRound = ((this.solves.length - 1) % 5) + ((Object.entries(this.solves[this.solves.length - 1].times).filter(([,v]) => (v.time || v.DNF)).length === Object.keys(this.solves[this.solves.length - 1].times).length) ? 1 : 0)
      if (solveNumInRound === 0 || solveNumInRound === 1) return mlStat // no stats for solve 0, solve 1

      const roundStart = (Math.ceil(this.solves.length / 5) - 1) * 5

      // store the possible range of averages for each solver
      let roundSolves = {}
      roundSolves = this.solvers.reduce((acc, v) => {
        let times = [] // for computing mean and average
        for (let i = roundStart; i < roundStart + solveNumInRound; i++) {
          times.push(this.getSolveTime(this.solves[i].times[v._id]))
        }
        let bpa = 0
        let wpa = Infinity
        if (solveNumInRound === 4) {
          times.sort((a, b) => a - b)
          bpa = Math.round(times.slice(0, times.length - 1).reduce((acc, v) => acc + 100 * v, 0) / (solveNumInRound - 1)) / 100
          wpa = Math.round(times.slice(1, times.length).reduce((acc, v) => acc + 100 * v, 0) / (solveNumInRound - 1)) / 100
        }
        return {
          ...acc, 
          [v._id]: {bpa, wpa, times}}
      }, {})

      this.solvers.forEach((solver) => {
        let stat = 0
        let statType = ''
        let times = roundSolves[solver._id].times
        switch (solveNumInRound) {
          // MO2/MO3
          case 2:
          case 3:
            stat = Math.round(times.reduce((acc, v) => acc + 100 * v, 0) / solveNumInRound) / 100
            statType = `MO${solveNumInRound}`
            break

          // BPA/NTL (won/lost or trailing => best possible average, leading => need to lock)
          case 4: {
            let opponentBPA = Infinity, opponentWPA = 0
            this.solvers.forEach((opponent) => {
              if (opponent._id === solver._id) return // skip self
              if (roundSolves[opponent._id].bpa < opponentBPA) opponentBPA = roundSolves[opponent._id].bpa
              if (roundSolves[opponent._id].wpa > opponentWPA) opponentWPA = roundSolves[opponent._id].wpa
            })
            statType = 'BPA'
            if (roundSolves[solver._id].wpa < opponentBPA) statType = 'BPA' // won
            else if (roundSolves[solver._id].bpa > opponentWPA) statType = 'BPA' // lost
            else {
              let max = Math.round(times[times.length - 1] * 100) / 100
              let min = Math.round(times[0] * 100) / 100
              let sum = Math.round(times.reduce((acc, v) => acc + 100 * v, 0)) / 100
              let ntl = 3 * opponentBPA - sum + max + min - (0.02) // subtract 0.02 to account for ties. otherwise ntl will cause an exact tie. -0.02 will round down to the lower value. This is dependent on round size of 5.
              if (ntl >= min) {
                stat = ntl
                statType = 'NTL'
              }
            }

            if (statType === 'BPA') stat = roundSolves[solver._id].bpa
            break
          }

          // AVG - WCA Ao5
          case 5:
            times.sort((a, b) => a - b)
            stat = Math.round(times.slice(1, times.length - 1).reduce((acc, v) => acc + 100 * v, 0) / (solveNumInRound - 2)) / 100
            statType = `AVG`
            break
        }
        mlStat[solver._id].time = stat
        mlStat[solver._id].type = statType
      })
      return mlStat
    },
    monkeyLeagueRounds () {
      let mlRounds = this.solvers.reduce((acc, v) => ({...acc, [v._id]: {name: v.solver, total: 0, scores: []}}), {})
      let overtime = false
      if (!this.monkeyLeagueSeason) return mlRounds

      // SEASON 1
      if (this.monkeyLeagueSeason === 1) {
        for (let i = 0; i < this.solves.length; i += 5) {
          // if round isn't completed (ie, not enough solves or last solve in the round is still not fully solved)
          if ((i + 5 > this.solves.length) || (Object.entries(this.solves[i + 5 - 1].times).filter(([,v]) => (v.time || v.DNF)).length !== Object.keys(this.solves[i + 5 - 1].times).length)) {
            Object.entries(mlRounds).forEach(([, solver]) => {
              solver.scores.push({score: 0})
            })
            break
          }

          let roundScores = {}
          let bestSingle = Infinity, bestSingleId = ''
          let roundTimes = {}, bestAverage = Infinity, bestAverageId = ''
          for (let j = i; j < i + 5; j++) {
            // +1 point for winning each single
            let winner = this.winners[j]
            if (!(winner in roundScores)) roundScores[winner] = 0
            roundScores[winner]++

            // +1 points for best single
            Object.entries(this.solves[j].times).forEach(([id, time]) => {
              let currTime = this.getSolveTime(time)
              if (isNaN(currTime)) currTime = Infinity
              if (currTime < bestSingle) {
                bestSingle = currTime
                bestSingleId = id
              } else if (currTime === bestSingle && bestSingleId !== id) {
                bestSingleId = ''
              }
            })

            // +2 point for best average
            Object.entries(this.solves[j].times).forEach(([id, time]) => {
              if (!(id in roundTimes)) roundTimes[id] = []
              roundTimes[id].push(this.getSolveTime(time))
            })
          }

          Object.entries(roundTimes).forEach(([id, times]) => {
            if (times.length != 5) return
            times.sort((a, b) => a - b)
            let avg = Math.round((times.slice(1, times.length - 1).reduce((acc, v) => acc + (100 * v), 0)) / (times.length - 2)) / 100
            if (avg < bestAverage) {
              bestAverage = avg
              bestAverageId = id
            } else if (avg === bestAverage) {
              bestAverageId = ''
            }
          })

          let maxScore = 0, maxScoreId = ''
          const isCurrentRoundOvertime = overtime // copy the variable so that overtime won't be set for one solver but not for others.
          Object.entries(mlRounds).forEach(([id, solver]) => {
            let score = 0
            if (roundScores[id] === 5) score += 1
            if (bestSingleId === id) score += 1
            if (bestAverageId === id) score += 2
            solver.scores.push({score, overtime: isCurrentRoundOvertime, winner: false, roundEnd: i+5})
            solver.total += score
            if (solver.total >= this.mlWinScore) overtime = true
            if (score > maxScore) {
              maxScore = score
              maxScoreId = id
            } else if (score === maxScore) {
              maxScoreId = ''
            }
          })
          if (maxScoreId !== '') {
            mlRounds[maxScoreId].scores[mlRounds[maxScoreId].scores.length - 1].winner = true
            Object.entries(mlRounds).forEach(([,solver]) => {
              if (!solver.scores[solver.scores.length - 1].winner) solver.scores[solver.scores.length - 1].loser = true
            })
          }
        }
      }

      // SEASON 2
      if (this.monkeyLeagueSeason === 2) {
        let i = 0
        while (i < this.solves.length) {
          let roundScores = {}
          let maxScore = 0, maxScoreId = ''
          while (maxScore < this.mlRoundWinScore && i < this.solves.length) {
            let winner = this.winners[i]
            if (!(winner in roundScores)) roundScores[winner] = 0
            roundScores[winner]++

            if (maxScore === roundScores[winner]) {
              maxScoreId = ''
            } else if (maxScore < roundScores[winner]) {
              maxScore = roundScores[winner]
              maxScoreId = winner
            }
            i++
          }

          Object.entries(mlRounds).forEach(([id, solver]) => {
            if (maxScore !== this.mlRoundWinScore) {
              solver.scores.push({score: (roundScores[id] || 0)})
              return
            }
            let score = roundScores[id] || 0
            solver.scores.push({score, overtime: false, winner: maxScoreId === id, loser: maxScoreId !== id, roundEnd: i})
            solver.total += maxScoreId === id ? 1 : 0
          })
        }
      }
      return mlRounds
    },
  },
  created () {
    this.$store.state.socket.on('addTime', (data) => {
      this.addTimeToSession(data)
    })

    this.$store.state.socket.on('newSolver', (data) => {
      this.addSolverToSession(data)
    })

    this.$store.state.socket.on('newScramble', (data) => {
      this.addScrambleToSession(data)
    })

    this.$store.state.socket.on('deleteScramble', (data) => {
      this.deleteScrambleFromSession(data)
    })
  },
  mounted () {
    let averages = 'Ao5, Ao12, time'
    if (this.$route.query.stats) {
      averages = this.$route.query.stats
    } else {
      let sessionTimesSettings = this.$store.state.sessionTimesSettings
      if (sessionTimesSettings && sessionTimesSettings.averages) {
        averages = sessionTimesSettings.averages
      }
    }
    this.statsToCompute = averages.split(',').map(item => item.trim())
  },
  components: {
    SessionSingleStat,
    SessionSingleTime,
  }
}
</script>

<style>
.bold-solver {
  font-weight: bold !important;
  font-size: 1em !important;
  color: black !important;
}
.data-table table {
  border-collapse: collapse;
}
.round-barrier {
  border-bottom: thick solid rgba(0,0,0,1);
}
</style>
